import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import BuriedExampleImage from "@/assets/images/buriedExample.png";
import ExpandableSection from "@/components/job/ExpandableSection";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TextInput } from "@/components/ui/textInput";
import { inputStep, maxInputNumber } from "@/constants/inputs";
import { useConfigOptions } from "@/hooks/useConfigOptions";
import useCUCompositeJobStore from "@/store/useCUCompositeJobStore";
import { getFloat } from "@/utils/mappings";

const buriedSchema = z.object({
  buried_depth: z.union([
    z.number().lt(maxInputNumber),
    z.string().refine((value) => !isNaN(Number.parseFloat(value)), { message: "Must be a number" }),
  ]),
  unit_for_buried_check: z.string().min(1, "Unit for buried check is required"),
});

type BuriedSchema = z.infer<typeof buriedSchema>;
const cardName = "buriedDetails";
export function BuriedDetails() {
  const { measureUnitOptions } = useConfigOptions();
  const { sections, setBuriedDetails, toggleExpandableSection } = useCUCompositeJobStore();
  const savedBuriedSection = sections.buriedDetails;

  const form = useForm<BuriedSchema>({
    resolver: zodResolver(buriedSchema),
    values: {
      buried_depth: savedBuriedSection?.data?.buried_depth ?? "",
      unit_for_buried_check: savedBuriedSection?.data.unit_for_buried_check || "Inches",
    },
  });
  const { errors } = form.formState;

  const saveBuried = (data: BuriedSchema) => {
    if (!data.unit_for_buried_check || !data.buried_depth) {
      return;
    }
    setBuriedDetails({
      // @ts-expect-error
      data,
      status: "done",
    });
  };

  return (
    <ExpandableSection
      status={savedBuriedSection?.status ?? "hidden"}
      setIsExpanded={() => toggleExpandableSection(cardName)}
      title="Buried Details"
    >
      <Form {...form}>
        <form className="flex flex-col gap-4" onSubmit={form.handleSubmit(saveBuried)}>
          <div className="flex flex-col gap-4 md:flex-row">
            <TextInput
              className="flex-1"
              // className="mr-4 flex-1 basis-full md:basis-60"
              label="Buried Depth"
              type="number"
              step={inputStep}
              error={errors.buried_depth?.message}
              {...form.register("buried_depth", { setValueAs: getFloat })}
            />
            <FormField
              control={form.control}
              name="unit_for_buried_check"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel>Unit</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value ?? undefined}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Unit" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {measureUnitOptions.map((option) => {
                        return (
                          <SelectItem key={option.value} value={option.value}>
                            {option.title}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="ml-auto flex justify-between gap-2">
              <img
                src={BuriedExampleImage}
                className="w-[326px] md:w-[230px]"
                height="161px"
                alt="Buried example image"
              />
            </div>
          </div>

          <Button className="w-full self-end md:w-fit" type="submit">
            Save
          </Button>
        </form>
      </Form>
    </ExpandableSection>
  );
}
